import { ColumnDef, ColumnFiltersState, ColumnOrderState } from '@tanstack/react-table';

import { ResourceType, Transaction } from '@/cip/_openapi/portfolio-optimizer';
import { useTransactions } from '@/cip/api/transaction';
import { transactionsListColumns } from '@/cip/Transactions/transactionsListColumns';
import { ServerDataTable } from '@/components/table/DataTable';
import { useRequiredParams } from 'utils';

import { AccountNavCash } from './AccountNavCash';
import {
    accountTableClassName,
    AccountTableLayout,
    DEFAULT_ACCOUNT_TABLE_COLUMN_ORDER,
    DEFAULT_ACCOUNT_TABLE_COLUMN_VISIBILITY,
} from './accountTableDefaults';
import { AccountTableToolbar } from './AccountTableToolbar';
import { useAccountTableState } from './useAccountTableState';

const TRANSACTIONS_DEFAULT_COLUMN_VISIBILITY = {
    ...DEFAULT_ACCOUNT_TABLE_COLUMN_VISIBILITY,
    tradeDate: true,
    subType: false,
    assetDisplayName: false,
    closePrice: false,
    cusip: false,
    isin: false,
    transferAccount: false,
    transactionId: false,
    currencyPrimary: false,
    aC: false,
};

const EXCLUDE_TRANSACTION_COLUMNS = ['pId', 'portfolioGuid', 'portfolioName', 'householdName'];

interface AccountTransactionsTableProps {
    tableId: string;
    variant?: 'all' | 'cash' | 'transfers';
    layout?: AccountTableLayout;
    columns?: ColumnDef<Transaction, any>[];
    columnOrder?: ColumnOrderState;
    initialColumnFilters?: ColumnFiltersState;
    emptyMessage?: string;
}

export const AccountTransactionsTable = ({
    tableId,
    variant = 'all',
    layout = 'legacy',
    columns = transactionsListColumns,
    columnOrder: defaultColumnOrder = DEFAULT_ACCOUNT_TABLE_COLUMN_ORDER,
    initialColumnFilters,
    emptyMessage = 'No transactions found',
}: AccountTransactionsTableProps) => {
    const { accountGUID } = useRequiredParams(['accountGUID']);
    const {
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        resetSettings,
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        accountFilter,
        allFilters,
    } = useAccountTableState({
        tableId,
        accountGUID,
        excludeColumns: EXCLUDE_TRANSACTION_COLUMNS,
        defaultColumnVisibility: TRANSACTIONS_DEFAULT_COLUMN_VISIBILITY,
        defaultColumnOrder,
        initialColumnFilters,
    });

    const {
        data: transactionsPage,
        isLoading,
        isFetching,
    } = useTransactions({
        columnFilters: allFilters,
        pagination,
        sorting,
        dataVariant: variant,
    });

    return (
        <ServerDataTable
            data={transactionsPage?.transactions || []}
            columns={columns}
            state={{ pagination, sorting, columnFilters, columnVisibility, columnOrder, columnSizing }}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            onColumnFiltersChange={setColumnFilters}
            onColumnVisibilityChange={setColumnVisibility}
            onColumnOrderChange={setColumnOrder}
            onColumnSizingChange={setColumnSizing}
            isLoading={isLoading}
            isFetching={isFetching}
            emptyMessage={emptyMessage}
            className={accountTableClassName(layout)}
            rowCount={transactionsPage?.totalNr ?? 0}
            deferInitialRender={true}
            renderToolbar={(table) => (
                <AccountTableToolbar
                    table={table}
                    layout={layout}
                    resourceType={ResourceType.TRANSACTION}
                    additionalFilters={accountFilter}
                    excludeColumns={EXCLUDE_TRANSACTION_COLUMNS}
                    onReset={resetSettings}
                    leftContent={<AccountNavCash accountGUID={accountGUID} />}
                />
            )}
        />
    );
};
